import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

const COHORT_PHOTO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/ngjpcxu0_IMG_20260318_205714_2.webp";
const WEAVE_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/mur72jzi_WEAVE-LOGO%202024.webp";
const ESSEX_STARTUPS_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/9pfl78q8_Logo.jpg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const expired = new URLSearchParams(location.search).get("expired") === "1";
  const [tab, setTab] = useState("team");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      const next = new URLSearchParams(location.search).get("next");
      const isMentor = result?.user?.role === "mentor";
      const to = next || location.state?.from?.pathname || (isMentor ? "/mentor/dashboard" : "/dashboard");
      navigate(to, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#0F1F2E]" data-testid="login-page">
      {/* Left hero: cohort photo + logos overlay */}
      <div
        className="hidden md:flex items-stretch justify-stretch relative overflow-hidden bg-[#0F1F2E]"
        data-testid="login-hero-panel"
      >
        <img
          src={COHORT_PHOTO}
          alt="Essex iTeams cohort at their final pitch celebration"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F1F2E]/85 via-[#0F1F2E]/30 to-transparent" />

        <div className="absolute top-8 left-8 flex items-center gap-4">
          <img src={ESSEX_STARTUPS_LOGO} alt="Essex Startups" className="h-14 w-14 object-cover" />
          <img src={WEAVE_LOGO} alt="The Weave" className="h-14 w-auto object-contain drop-shadow-lg" />
        </div>

        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="inline-block text-[10px] font-semibold tracking-[0.24em] uppercase bg-[#F4B847] text-[#0F1F2E] px-3 py-1 mb-4">
            iTeams · University of Essex
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tight leading-[0.95] drop-shadow-lg">
            Real challenges.<br/>
            <span className="text-[#F4B847]">Real businesses.</span><br/>
            Real teams.
          </h2>
          <p className="mt-4 text-sm md:text-base opacity-90 max-w-md leading-relaxed">
            Eight sessions over seven weeks with a sponsor business. Mentors alongside you.
            One final pitch that changes what you thought you could do.
          </p>
        </div>
      </div>

      {/* Right: sign-in */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-[#FDFDFD]">
        <div className="w-full max-w-md">
          {/* Mobile mini-hero */}
          <div className="md:hidden mb-8 -mx-6 -mt-6 relative">
            <img src={COHORT_PHOTO} alt="iTeams cohort" className="w-full object-cover" style={{maxHeight: 200}}/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1F2E]/80 to-transparent" />
            <div className="absolute bottom-3 left-4 flex items-center gap-2">
              <img src={ESSEX_STARTUPS_LOGO} alt="" className="h-8 w-8 object-cover"/>
              <img src={WEAVE_LOGO} alt="" className="h-8 w-auto"/>
            </div>
          </div>

          <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#25AAAA]">Sign in</div>
          <h1 className="font-display font-black tracking-tighter mt-2 text-[#0F1F2E] leading-[0.95]" data-testid="login-headline">
            {tab === "admin" ? (
              <><span className="block text-3xl sm:text-4xl">Welcome,</span><span className="block text-4xl sm:text-5xl mt-1">admin.</span></>
            ) : tab === "mentor" ? (
              <><span className="block text-3xl sm:text-4xl">Welcome,</span><span className="block text-4xl sm:text-5xl mt-1">mentor.</span></>
            ) : (
              <><span className="block text-3xl sm:text-4xl">Welcome to</span><span className="block text-5xl sm:text-6xl mt-1 text-[#25AAAA]">iTeams.</span></>
            )}
          </h1>
          <p className="mt-3 text-sm text-[#525252] leading-relaxed">
            {tab === "admin"
              ? "Manage the programme, sessions, sponsors, teams and mentors."
              : tab === "mentor"
              ? "Head to your dashboard to see your teams and share reports."
              : "New here? Register first — your admin will have added your name to the roster."}
          </p>

          <div className="mt-6 grid grid-cols-3 border border-[#0F1F2E]" data-testid="login-tabs">
            <button type="button" onClick={() => setTab("team")} data-testid="login-tab-team"
              className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                tab === "team" ? "bg-[#0F1F2E] text-white" : "bg-white text-[#0F1F2E] hover:bg-[#F3F4F6]"
              }`}>Team</button>
            <button type="button" onClick={() => setTab("mentor")} data-testid="login-tab-mentor"
              className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors border-l border-r border-[#0F1F2E] ${
                tab === "mentor" ? "bg-[#0F1F2E] text-white" : "bg-white text-[#0F1F2E] hover:bg-[#F3F4F6]"
              }`}>Mentor</button>
            <button type="button" onClick={() => setTab("admin")} data-testid="login-tab-admin"
              className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors ${
                tab === "admin" ? "bg-[#0F1F2E] text-white" : "bg-white text-[#0F1F2E] hover:bg-[#F3F4F6]"
              }`}>Admin</button>
          </div>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@essex.ac.uk"
                className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#25AAAA] focus-visible:border-[#25AAAA] h-11"
                data-testid="login-email-input"/>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Password</Label>
                <Link to="/forgot" className="text-[11px] uppercase tracking-wider text-[#25AAAA] hover:text-[#1e7d7d] font-semibold" data-testid="link-forgot-password">
                  Forgot?
                </Link>
              </div>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none border-[#E5E7EB] focus-visible:ring-[#25AAAA] focus-visible:border-[#25AAAA] h-11"
                data-testid="login-password-input"/>
            </div>

            {expired && !error && (
              <div className="border border-[#f4b847] bg-[#FEF3D0] px-3 py-2 text-sm text-[#7A5B17]" data-testid="login-expired-banner">
                Your session expired — please sign in again.
              </div>
            )}
            {error && (
              <div className="border border-[#E93E36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F]" data-testid="login-error">{error}</div>
            )}

            <Button type="submit" disabled={loading}
              className="w-full h-11 rounded-none bg-[#25AAAA] text-white hover:bg-[#1e7d7d] font-semibold tracking-wide uppercase text-xs"
              data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-[#525252]">
            {tab === "team" ? (
              <>Don't have an account?{" "}
                <Link to="/register" className="text-[#25AAAA] font-medium underline-offset-4 hover:underline" data-testid="link-register">
                  Register as a student
                </Link>
              </>
            ) : tab === "mentor" ? (
              <span className="italic">Mentor accounts are set up by admin. If you don't have one yet, ask them to invite you.</span>
            ) : (
              <span className="italic">Admin accounts are provisioned by the Weave team.</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
